import { ReactNode } from 'react';
import { DndContext, closestCenter } from '@dnd-kit/core';
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers';
import { useDragAndDrop } from '../hooks/useDragAndDrop';
import {
  useReactTableController,
  useReactTableControllerInstance,
} from '../hooks/useReactTableController';
import type { Updater, ColumnOrderState } from '@tanstack/react-table';

interface DragDropContainerProps {
  children: ReactNode;
  tableId?: string;
}

/**
 * Container component that provides drag and drop functionality for table columns
 */
export function DragDropContainer({ children, tableId }: DragDropContainerProps) {
  // Если tableId не передан, просто рендерим детей без DnD
  if (!tableId) {
    return <>{children}</>;
  }

  const columnOrder = useReactTableController(tableId, s => s.columnOrder);
  const controller = useReactTableControllerInstance(tableId);

  // Обертка для setColumnOrder с поддержкой updater functions
  // Важно: берем актуальное значение из контроллера, а не из замыкания
  const setColumnOrderWrapper = (updater: Updater<ColumnOrderState>) => {
    const currentColumnOrder = controller.getSnapshot().columnOrder;
    const next = typeof updater === 'function' ? updater(currentColumnOrder) : updater;
    controller.setColumnOrder(next);
  };

  const { sensors, handleDragEnd } = useDragAndDrop(columnOrder, setColumnOrderWrapper);

  return (
    <DndContext
      collisionDetection={closestCenter}
      modifiers={[restrictToHorizontalAxis]}
      onDragEnd={handleDragEnd}
      sensors={sensors}
    >
      {children}
    </DndContext>
  );
}
